import { faXmark } from '@fortawesome/free-solid-svg-icons';
import { cn } from '@frontend/shadcn/lib/utils';
import {
  Drawer,
  DrawerContent,
  DrawerTitle,
  DrawerHeader,
  DrawerClose,
  DrawerFooter,
} from '@frontend/shadcn/components/ui/drawer';
import { Button } from '@frontend/shadcn/components/ui/button';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React from 'react';
import { P } from '@frontend/shadcn/components/typography/p';
import { ProgramOnboardingTemplateType } from '@frontend/app/types/globalTypes';
import { H4 } from '@frontend/shadcn/components/typography/h4';
import { useHistory } from 'react-router-dom';
import { useToast } from '@frontend/shadcn/hooks/use-toast';
import { AmplifyIcon, PulseIcon } from './TemplateIcons';
import { useCreateProgramOnboardingTemplate } from '../hooks/useCreateProgramOnboardingTemplate';
import { SelectableCardGroup } from '../../components/SelectableCardGroup';
import { SettingsRoutes } from '../../../constants';

const amplifyTemplate = 'https://storage.googleapis.com/aspirex-static-files/onboarding-template/amplifyTemplate.png';
const pulseTemplate = 'https://storage.googleapis.com/aspirex-static-files/onboarding-template/pulseTemplate.png';

const { useState } = React;

type TProps = {
  projectId: number;
  isOpen: boolean;
  onClose: () => void;
  onClickDuplicateApplication: () => void;
};

export const NewApplicationDrawer = (props: TProps) => {
  const { projectId, isOpen, onClose, onClickDuplicateApplication } = props;
  const { toast } = useToast();
  const [templateType, setTemplateType] = useState<ProgramOnboardingTemplateType>(
    ProgramOnboardingTemplateType.AMPLIFY,
  );

  const history = useHistory();

  const [createProgramOnboardingTemplate, { loading }] = useCreateProgramOnboardingTemplate();

  const handleTemplateChange = (value: ProgramOnboardingTemplateType) => {
    setTemplateType(value as ProgramOnboardingTemplateType);
  };

  const templateOptions = React.useMemo(() => {
    const options = [
      {
        id: 1,
        value: ProgramOnboardingTemplateType.PULSE,
        renderBody: (
          <div className="flex items-center gap-4">
            <PulseIcon />
            <div className="flex flex-col">
              <H4 className="font-medium text-base">Pulse</H4>
              <P className="text-xs font-normal text-grey-5 mb-0">
                A simple and easy to view application page that has less requirements.
              </P>
            </div>
          </div>
        ),
      },
      {
        id: 2,
        value: ProgramOnboardingTemplateType.AMPLIFY,
        renderBody: (
          <div className="flex items-center gap-4">
            <AmplifyIcon />
            <div className="flex flex-col">
              <H4 className="font-medium text-base">Amplify</H4>
              <P className="text-xs font-normal text-grey-5 mb-0">
                Focus on the look and feel of your application page with robust controls.
              </P>
            </div>
          </div>
        ),
      },
    ];

    return options;
  }, []);

  const onLaunch = async () => {
    try {
      const { data } = await createProgramOnboardingTemplate({
        variables: {
          input: { programId: projectId, templateType },
        },
      });
      toast({
        variant: 'success',
        title: 'Application created successfully',
        duration: 1000,
        className: cn('top-0 right-0 flex fixed md:max-w-[420px] md:top-4 md:right-4'),
      });
      if (data?.template?.id) {
        history.push(SettingsRoutes.recruiting.template.replace(':projectId', projectId.toString()).replace(':templateId', data.template.id.toString()));
      }
    } catch (error) {
      toast({
        variant: 'error',
        title: 'Error creating application',
        duration: 1000,
        className: cn('top-0 right-0 flex fixed md:max-w-[420px] md:top-4 md:right-4'),
      });
    } finally {
      onClose();
    }
  };

  return (
    <Drawer open={isOpen} direction="right" onOpenChange={onClose}>
      <DrawerContent
        className={cn(
          'fixed left-auto inset-y-0 mt-0 right-0 z-[9999] flex h-full w-[576px] flex-col border-l bg-background rounded-none p-0',
          'duration-300 ease-in-out data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:slide-out-to-right data-[state=open]:slide-in-from-right',
          'bg-background',
          'top-[var(--header-height,0px)] h-[calc(100vh-var(--header-height,0px))]',
          'bg-white',
        )}
        data-vaul-no-drag
      >
        <DrawerHeader className="flex justify-between items-center border-b border-grey-3 px-6 py-4">
          <DrawerTitle className="font-medium mb-0">New Application</DrawerTitle>
          <DrawerClose asChild>
            <Button
              variant="ghost"
              size="headerIcon"
              className="border border-transparent hover:border-grey-6 hover:bg-transparent"
            >
              <FontAwesomeIcon icon={faXmark} />
            </Button>
          </DrawerClose>
        </DrawerHeader>
        <div className="flex flex-col gap-4 p-6 overflow-y-scroll">
          <P className="text-sm font-regular text-grey-6 mb-0">
            Customize your application page to generate inbound interest for your campaign and publish it to Aspire's
            Creator's Marketplace.
          </P>

          <SelectableCardGroup
            value={templateType}
            options={templateOptions}
            onChange={handleTemplateChange}
            orientation="horizontal"
          />
          <div className="flex items-center justify-center bg-grey-1 rounded-2xl">
            <img
              src={templateType === ProgramOnboardingTemplateType.AMPLIFY ? amplifyTemplate : pulseTemplate}
              alt="amplifyTemplate"
              className="w-[280px] h-[560px] object-contain"
            />
          </div>
          <P className="text-sm font-medium text-grey-6 mb-0 underline cursor-pointer" onClick={onClickDuplicateApplication}>
            Duplicate an Application Page
          </P>
        </div>
        <DrawerFooter className="flex flex-row gap-2 space-x-2 border-t border-grey-3">
          <Button onClick={onLaunch} disabled={loading}>
            Launch
          </Button>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
        </DrawerFooter>
      </DrawerContent>
    </Drawer>
  );
};
